import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence, Body,
  PrimaryCTA, Section, Hairline,
} from "./MarketingLayout";

const PLANS = [
  { id: "starter",   label: "Starter — free during beta" },
  { id: "residence", label: "Residence — $14/mo" },
  { id: "founding",  label: "Founding Member — $99/yr (lifetime)" },
];

const HOUSEHOLD_SIZES = ["1", "2", "3", "4", "5", "6+"];

export default function JoinBetaPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialPlan = params.get("plan") || "residence";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [household, setHousehold] = useState("");
  const [plan, setPlan] = useState(initialPlan);
  const [source, setSource] = useState("");
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [position, setPosition] = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/waitlist/count`).then(r => r.json()).then(j => setCount(j.count)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("A first name and email — that's all we need.");
      return;
    }
    setBusy(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName, last_name: lastName, email,
          country, household_size: household,
          interested_plan: plan, referral_source: source,
          marketing_consent: consent, beta_interest: true,
        }),
      });
      if (!res.ok) throw new Error("bad response");
      try {
        const data = await res.json();
        if (data && typeof data.position === "number" && data.position > 0) setPosition(data.position);
      } catch { /* position is optional */ }
      setSent(true);
    } catch {
      toast.error("Could not save just now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <MarketingLayout testid="join-beta-page">
        <Section testid="join-beta-thankyou" className="pt-24 sm:pt-32 pb-32">
          <div className="max-w-[52ch]">
            <Eyebrow>Received</Eyebrow>
            <Display className="mt-6" data-testid="join-beta-thankyou-title">
              Welcome home, {firstName.split(" ")[0]}.
            </Display>
            <FocalSentence className="mt-8 max-w-[42ch]">
              Your place on the Founding Member list is held.
            </FocalSentence>
            {position !== null && (
              <Body className="mt-4" data-testid="join-beta-position">
                You're household number {position} on the founding list.
              </Body>
            )}
            <Body className="mt-6">
              Early access invitations are released in small groups so every
              household arrives at a Residence that already feels quiet. When
              your turn arrives you’ll receive a short, warm email from us at{" "}
              <strong style={{ color: 'var(--ara-ink, #1F1F1F)' }}>{email}</strong>.
            </Body>
            <Body className="mt-4" style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>
              We open a small cohort roughly every two weeks. Please add
              <strong style={{ color: 'var(--ara-ink, #1F1F1F)' }}> hello@arahome.co</strong> to your contacts so our invitation doesn’t land in junk.
            </Body>
            <div className="mt-10">
              <PrimaryCTA to="/">Return to the front door</PrimaryCTA>
            </div>
          </div>
        </Section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout testid="join-beta-page">
      <Section testid="join-beta-hero" className="pt-24 sm:pt-32 pb-12">
        <div className="max-w-[820px]">
          <Eyebrow>Founding Members</Eyebrow>
          <Display className="mt-6">Start your Residence.</Display>
          <FocalSentence className="mt-8 max-w-[46ch]">
            A small group of households helping shape ARA from its very first day.
          </FocalSentence>
          {count !== null && count > 0 && (
            <Body className="mt-6" data-testid="join-beta-count">
              {count} {count === 1 ? "household has" : "households have"} already joined the list.
            </Body>
          )}
        </div>
      </Section>

      <Hairline />

      <Section testid="join-beta-form-section">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-x-16 gap-y-8">
          <div>
            <Eyebrow>What happens next</Eyebrow>
            <ol className="mt-6 space-y-4 text-[13.5px] max-w-[36ch]" style={{ color: 'var(--ara-ink-muted, #4A4744)' }}>
              <li>1 · We hold your place on the list.</li>
              <li>2 · Invitations are released in small groups.</li>
              <li>3 · You receive a warm email when your Residence is ready.</li>
              <li>4 · Founding Member pricing is locked from day one.</li>
            </ol>
          </div>

          <form onSubmit={submit} className="max-w-[56ch] space-y-6" data-testid="join-beta-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="First name" value={firstName} onChange={setFirstName} testid="join-first-name" required />
              <Field label="Last name" value={lastName} onChange={setLastName} testid="join-last-name" />
            </div>
            <Field label="Email" type="email" value={email} onChange={setEmail} testid="join-email" required />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Country" value={country} onChange={setCountry} testid="join-country" placeholder="Aotearoa, Australia…" />
              <Select label="Household size" value={household} onChange={setHousehold} options={HOUSEHOLD_SIZES} testid="join-household-size" />
            </div>
            <Select label="Which plan interests you?" value={plan} onChange={setPlan} options={PLANS.map(p => ({ id: p.id, label: p.label }))} testid="join-plan" />
            <Field label="How did you hear about ARA? (optional)" value={source} onChange={setSource} testid="join-source" placeholder="A friend, a post, a family member…" />

            <label className="flex items-baseline gap-3 text-[13px] cursor-pointer" style={{ color: 'var(--ara-ink-muted, #4A4744)' }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
                style={{ accentColor: 'var(--ara-ink, #1F1F1F)' }}
                data-testid="join-consent"
              />
              <span>
                Send me the occasional quiet update as ARA grows. No noise.
                Unsubscribe any time.
              </span>
            </label>

            <div className="pt-2">
              <PrimaryCTA type="submit" onClick={submit} testid="join-beta-submit">
                {busy ? "Holding your place…" : "Join the Founding Member Waitlist →"}
              </PrimaryCTA>
            </div>

            <p className="text-[11.5px]" style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>
              We store your name and email so we can invite you. Nothing else.
              See our <a href="#" style={{ borderBottom: '1px solid var(--ara-line, rgba(31,31,31,0.2))' }}>privacy note</a>.
            </p>
          </form>
        </div>
      </Section>

      <Hairline />

      <Section testid="join-beta-final" className="pb-32">
        <FocalSentence className="max-w-[52ch]">
          Founding Members receive lifetime pricing, direct access to the
          team, and a small recognition in the Residence Colophon.
        </FocalSentence>
      </Section>
    </MarketingLayout>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, required }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}>
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full bg-transparent outline-none py-2 text-[15px]"
        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink, #1F1F1F)', borderBottom: '1px solid var(--ara-line, rgba(31,31,31,0.15))' }}
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent outline-none py-2 text-[15px]"
        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink, #1F1F1F)', borderBottom: '1px solid var(--ara-line, rgba(31,31,31,0.15))' }}
        data-testid={testid}
      >
        <option value="">Choose…</option>
        {options.map(o => {
          const val = typeof o === 'string' ? o : o.id;
          const lbl = typeof o === 'string' ? o : o.label;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    </div>
  );
}
